import { Button, Checkbox, Modal } from 'components/core';
import {
  downloadPdf,
  formatIteroDateTime,
  removeSpaces,
} from 'components/ITeroTermsConditionsModal/ITeroTermsConditionsModal.helper';
import React, { FC, useRef, useState } from 'react';
import { MdClose } from 'react-icons/md';
import { useElementVisibility, useReadOnlyUser } from 'utils/hooks';
import {
  ITeroContract,
  ITeroRentalContract,
  RentalOrderDetailsTable,
  ServicePlanTerm,
  StandardOrderDetailsTable,
  Styled,
} from './';
import { ITeroTermsConditionsModalProps } from './ITeroTermsConditionsModal.types';
import {
  getServicePlanFee,
  shouldShowServicePlanDetails,
} from 'components/ITeroProposal';
import handleITeroCustomerNeeds from 'utils/handleITeroCustomerNeeds';
import { useContentful, useDoctorProfile } from 'reactQuery/queries';
import { useI18n } from 'contexts/i18n';
import { isLicenseFieldAvailable } from 'components/ITeroCheckout';
import { isEMEAStore } from 'utils';
import ITeroTermsConditionsModalEMEA from 'components/ITeroTermsConditionsModalEMEA/ITeroTermsConditionsModalEMEA';

const ITeroTermsConditionsModal: FC<ITeroTermsConditionsModalProps> = ({
  onAccept,
  onClose,
  additionalOrderInfo,
  selectedScannerSubFundingSource,
  selectedShippingAddress,
  selectedBillingAddress,
  doctorProfile,
  currentProposal,
  selectedCreditCard,
}) => {
  const [isButtonEnabled, setIsButtonEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const legalTextRef = useRef<HTMLDivElement>(null);

  const { data: contentful } = useContentful();
  const { isReadOnlyUser } = useReadOnlyUser();
  const { data: doctor } = useDoctorProfile();
  const i18n = useI18n();

  const showLicenceFields = isLicenseFieldAvailable(
    additionalOrderInfo.licenceNumber,
    additionalOrderInfo.stateOfIssuance
  );

  const elementVisibilityCallback = () => {
    setIsButtonEnabled(true);
  };

  const lastElement = useElementVisibility(() => {
    elementVisibilityCallback();
  });

  const formattedUserTime = formatIteroDateTime();

  const onAcceptHandler = async () => {
    const responseDoc = await downloadPdf(
      'ACCEPTED',
      setIsLoading,
      legalTextRef,
      doctorProfile,
      additionalOrderInfo,
      formattedUserTime,
      i18n
    );
    onAccept(responseDoc);
  };

  const isMultipleScannerSubscriptions =
    currentProposal.lineitems.filter(item => item.subscriptions?.length > 0)
      .length > 1;

  let paymentOption = selectedScannerSubFundingSource ?? '';

  if (selectedScannerSubFundingSource === '3rd Party Financing') {
    paymentOption = paymentOption + ` (${currentProposal.financingCompany})`;
  }

  const isEPTPayment = currentProposal.scannerSubFundingSource === 'EPT';

  const {
    isSerialNumberRequired,
    isServiceTermHideRequired,
    isFuse25,
    isFuse45,
    isRental,
    isLumina,
    isLuminaGoBeyondRentConvert,
    isSprintRay,
  } = handleITeroCustomerNeeds(
    currentProposal,
    contentful?.contentfulEnvVariables
  );

  const servicePlanFee = getServicePlanFee(currentProposal);

  // Check if the text is in all caps and add letter spacing to fix the weird spacing issue in the generated PDF by jsPDF
  const elements = legalTextRef.current?.querySelectorAll('*');
  elements?.forEach(element => {
    (element as HTMLElement).style.fontFamily =
      "Arial, Helvetica, 'Times New Roman', serif";
  });

  // EMEA/NA router: this file is the single entry point for both regions.
  // State initialized above (legalTextRef, isButtonEnabled, onAcceptHandler, etc.)
  // is passed to the EMEA modal as props. Changes to that state logic affect EMEA behavior.
  return isEMEAStore() ? (
    <ITeroTermsConditionsModalEMEA
      setIsLoading={setIsLoading}
      legalTextRef={legalTextRef}
      doctorProfile={doctorProfile}
      additionalOrderInfo={additionalOrderInfo}
      localUserTime={formattedUserTime}
      i18n={i18n}
      onClose={onClose}
      currentProposal={currentProposal}
      selectedBillingAddress={selectedBillingAddress}
      selectedShippingAddress={selectedShippingAddress}
      isMultipleScannerSubscriptions={isMultipleScannerSubscriptions}
      paymentOption={currentProposal.scannerFundingSource} // EU doctors does not have sub funding source
      contentful={contentful}
      lastElement={lastElement}
      isLoading={isLoading}
      onAcceptHandler={onAcceptHandler}
      isButtonEnabled={isButtonEnabled}
      isReadOnlyUser={isReadOnlyUser}
      isEPTPayment={isEPTPayment}
    />
  ) : (
    <Modal overlaysize={'large'}>
      <Styled.Container>
        <Styled.TitleContainer>
          <Styled.LinkTag
            onClick={() =>
              downloadPdf(
                'PREVIEW',
                setIsLoading,
                legalTextRef,
                doctorProfile,
                additionalOrderInfo,
                formattedUserTime,
                i18n
              )
            }
          >
            {i18n['Preview']}
          </Styled.LinkTag>
          <Styled.CloseButton onClick={onClose}>
            <MdClose size={20} />
          </Styled.CloseButton>
        </Styled.TitleContainer>
        <Styled.LegalTextContainer ref={legalTextRef}>
          <div>
            <Styled.Title>{i18n['iTERO ORDER FORM']}</Styled.Title>
            <p>
              {doctorProfile?.country === 'CA'
                ? i18n['This iTero Order Form ("Order Form") - CA']
                : i18n['This iTero Order Form ("Order Form") - US']}
            </p>
            <p>
              <u>{i18n['Customer Information']}</u>
            </p>
            <Styled.FormGrid cellPerRow={2}>
              <Styled.FormGridItem>
                {additionalOrderInfo.doctorName}
              </Styled.FormGridItem>
              <Styled.FormGridItem>{formattedUserTime}</Styled.FormGridItem>
              <Styled.FormGridItem>
                {i18n['Business/Doctor Name']} {i18n['("Customer")']}
              </Styled.FormGridItem>
              <Styled.FormGridItem>{i18n['Date']}</Styled.FormGridItem>
            </Styled.FormGrid>
            <Styled.FormGrid cellPerRow={2}>
              <Styled.FormGridItem>
                {additionalOrderInfo.doctorPhone}
              </Styled.FormGridItem>
              <Styled.FormGridItem>
                {removeSpaces(additionalOrderInfo.contactEmail)}
              </Styled.FormGridItem>
              <Styled.FormGridItem>{i18n['Phone Number']}</Styled.FormGridItem>
              <Styled.FormGridItem>{i18n['Email Address']}</Styled.FormGridItem>
            </Styled.FormGrid>
            <Styled.FormGrid cellPerRow={1}>
              <Styled.FormGridItem>
                {removeSpaces(additionalOrderInfo.scannerLoginEmail)}
              </Styled.FormGridItem>
              <Styled.FormGridItem>
                {i18n['Scanner Registration Email']}
              </Styled.FormGridItem>
            </Styled.FormGrid>
            <Styled.FormGrid cellPerRow={1}>
              <Styled.FormGridItem>
                {selectedBillingAddress?.mailingName},{' '}
                {selectedBillingAddress?.address1}{' '}
                {selectedBillingAddress?.address2}{' '}
                {selectedBillingAddress?.address3}{' '}
                {selectedBillingAddress?.city},{' '}
                {selectedBillingAddress?.stateName}{' '}
                {selectedBillingAddress?.zipcode},{' '}
                {selectedBillingAddress?.countryIso}
              </Styled.FormGridItem>
              <Styled.FormGridItem>
                {i18n['Office / Headquarters Address']}
              </Styled.FormGridItem>
            </Styled.FormGrid>
            {/* TODO: get proper address, currently its the same selectedBillingAddress */}
            <Styled.FormGrid cellPerRow={1}>
              <Styled.FormGridItem>
                {selectedBillingAddress?.mailingName},{' '}
                {selectedBillingAddress?.address1}{' '}
                {selectedBillingAddress?.address2}{' '}
                {selectedBillingAddress?.address3}{' '}
                {selectedBillingAddress?.city},{' '}
                {selectedBillingAddress?.stateName}{' '}
                {selectedBillingAddress?.zipcode},{' '}
                {selectedBillingAddress?.countryIso}
              </Styled.FormGridItem>
              <Styled.FormGridItem>
                {i18n['Billing Address']} {i18n['(if different)']}
              </Styled.FormGridItem>
            </Styled.FormGrid>
            <Styled.FormGrid cellPerRow={3} noMarginBottom>
              <Styled.FormGridItem>
                {additionalOrderInfo.firstName} {additionalOrderInfo.lastName}
              </Styled.FormGridItem>
              <Styled.FormGridItem>
                {additionalOrderInfo.directPhone}
              </Styled.FormGridItem>
              <Styled.FormGridItem>
                {removeSpaces(additionalOrderInfo.directEmail)}
              </Styled.FormGridItem>
              <Styled.FormGridItem>
                {i18n['Office Contact / Coordinator']}
              </Styled.FormGridItem>
              <Styled.FormGridItem>{i18n['Phone Number']}</Styled.FormGridItem>
              <Styled.FormGridItem>{i18n['Email Address']}</Styled.FormGridItem>
            </Styled.FormGrid>
            <div>
              {
                i18n[
                  'Receives communications regarding shipment, delivery, registration, training, and customer support'
                ]
              }
            </div>
            {isSerialNumberRequired && (
              <p>
                <br />
                {i18n['Trade in / Upgrade Scanner (SN)']}:{' '}
                {currentProposal.existingScannerSerialNumber}
                <br />
                <br />
              </p>
            )}
            <p>
              <u>{i18n['Shipping Details']}</u>
            </p>
            <Styled.FormGrid cellPerRow={1}>
              <Styled.FormGridItem>
                {additionalOrderInfo.doctorName}
              </Styled.FormGridItem>
              <Styled.FormGridItem>
                {i18n['Business/Doctor Name']}
              </Styled.FormGridItem>
            </Styled.FormGrid>
            <Styled.FormGrid cellPerRow={1}>
              <Styled.FormGridItem>
                {selectedShippingAddress?.mailingName},{' '}
                {selectedShippingAddress?.address1}{' '}
                {selectedShippingAddress?.address2}{' '}
                {selectedShippingAddress?.address3}{' '}
                {selectedShippingAddress?.city},{' '}
                {selectedShippingAddress?.stateName}{' '}
                {selectedShippingAddress?.zipcode},{' '}
                {selectedShippingAddress?.countryIso}
              </Styled.FormGridItem>
              <Styled.FormGridItem>{i18n['Address']}</Styled.FormGridItem>
            </Styled.FormGrid>
            {showLicenceFields && (
              <Styled.FormGrid cellPerRow={2}>
                <Styled.FormGridItem>
                  {additionalOrderInfo.licenceNumber}
                </Styled.FormGridItem>
                <Styled.FormGridItem>
                  {additionalOrderInfo.stateOfIssuance}
                </Styled.FormGridItem>
                <Styled.FormGridItem>
                  {i18n['License Number']}
                </Styled.FormGridItem>
                <Styled.FormGridItem>{i18n['Licensing Entity']}</Styled.FormGridItem>
              </Styled.FormGrid>
            )}
            <p>
              <u>
                {i18n['Order Details']} ({currentProposal.proposalName})
              </u>
            </p>
            {isRental ? (
              <RentalOrderDetailsTable currentProposal={currentProposal} />
            ) : (
              <StandardOrderDetailsTable
                currentProposal={currentProposal}
                isServiceTermHideRequired={isServiceTermHideRequired}
              />
            )}
            {shouldShowServicePlanDetails(currentProposal) && (
              <>
                <p>
                  <u>{i18n['Service Plan Details*']}</u>
                  <Styled.ServicePlanSubText>
                    {
                      i18n[
                        'Applicable after the initial service term expiration.'
                      ]
                    }
                  </Styled.ServicePlanSubText>
                </p>
                <Styled.Table>
                  <thead>
                    <tr>
                      <Styled.Th>{i18n['Product(s)']}</Styled.Th>
                      {isMultipleScannerSubscriptions && (
                        <Styled.Th>{i18n['Related Scanner']}</Styled.Th>
                      )}
                      <Styled.Th>{i18n['Quantity']}</Styled.Th>
                      <Styled.Th>{i18n['List Price']}</Styled.Th>
                      <Styled.Th>
                        {i18n['Total Promotion/Discount Amount']}
                      </Styled.Th>
                      <Styled.Th>{i18n['Total Price']}</Styled.Th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentProposal?.lineitems.map(item => (
                      <React.Fragment key={item.name}>
                        {item.subscriptions?.map(subscription => (
                          <React.Fragment key={subscription.name}>
                            <tr>
                              <Styled.Td>
                                <div>{subscription.name}</div>
                                <br />
                                <div>
                                  <div>
                                    <strong>
                                      {i18n['Service Plan Fee Invoicing']}
                                    </strong>
                                    : {subscription.servicePlan}
                                  </div>
                                </div>
                              </Styled.Td>
                              {isMultipleScannerSubscriptions && (
                                <Styled.Td>{item.name}</Styled.Td>
                              )}
                              <Styled.Td>{subscription.quantity}</Styled.Td>
                              <Styled.Td>
                                {subscription.displayItemUnitPrice}
                              </Styled.Td>
                              <Styled.Td $width={'30%'}>
                                {subscription.adjustmentAmount && (
                                  <>
                                    {subscription.adjustmentAmount} <br />
                                  </>
                                )}
                                {subscription.adjustmentAmount &&
                                  subscription.promotionCode && (
                                    <>
                                      {subscription.promotionCode} <br />
                                    </>
                                  )}
                                <Styled.HelperText>
                                  {doctor?.country?.toUpperCase() === 'CA'
                                    ? i18n[
                                        'iTero Total Promotion Discount Table text - CA'
                                      ]
                                    : i18n[
                                        'iTero Total Promotion Discount Table text - US'
                                      ]}
                                </Styled.HelperText>
                              </Styled.Td>
                              <Styled.Td>
                                {subscription.displayItemTotal}
                              </Styled.Td>
                            </tr>
                          </React.Fragment>
                        ))}
                      </React.Fragment>
                    ))}
                  </tbody>
                </Styled.Table>

                <ServicePlanTerm servicePlanFee={servicePlanFee} />
              </>
            )}
            {isRental && (
              <>
                <br />
                <Styled.FormCheckboxContainer>
                  {i18n['Scanner Rental Payment Options']}:
                  <Checkbox
                    label={currentProposal.lineitems[0].servicePlan}
                    onChange={() => ({})}
                    checked={true}
                    disabled
                    style={{ color: 'black' }}
                  />
                </Styled.FormCheckboxContainer>
              </>
            )}
            <Styled.FormCheckboxContainer>
              {i18n['Payment Options']}:
              <Checkbox
                label={paymentOption}
                onChange={() => ({})}
                checked={true}
                disabled
                style={{ color: 'black' }}
              />
            </Styled.FormCheckboxContainer>
            {isRental ? (
              <>
                <div
                  dangerouslySetInnerHTML={{
                    __html: i18n['RentalPlanDisclaimer'],
                  }}
                />
                <br />
                <br />
                <ITeroRentalContract
                  doctorProfile={doctorProfile}
                  contentful={contentful}
                  isLumina={isLumina}
                />
              </>
            ) : (
              <>
                <div
                  dangerouslySetInnerHTML={{
                    __html: i18n['StandardServicePlanDisclaimer'],
                  }}
                />
                <br />
                <br />
                <ITeroContract
                  doctorProfile={doctorProfile}
                  contentful={contentful}
                  isEPTPayment={isEPTPayment}
                  isFuse25={isFuse25}
                  isFuse45={isFuse45}
                  isLuminaGoBeyondRentConvert={isLuminaGoBeyondRentConvert}
                  isSprintRay={isSprintRay}
                />
              </>
            )}
            <p ref={lastElement}></p>
          </div>
        </Styled.LegalTextContainer>
        {isLoading && (
          <Styled.LoadingOverlay>
            <Styled.PDFLoadingSpinner
              isPrimary={true}
              width={'40px'}
              height={'40px'}
            />
          </Styled.LoadingOverlay>
        )}
      </Styled.Container>
      <Styled.ButtonWrapper>
        <Button
          size="md"
          onClick={onAcceptHandler}
          disabled={!isButtonEnabled || isReadOnlyUser}
        >
          {i18n['I ACKNOWLEDGE AND AGREE']}
        </Button>
      </Styled.ButtonWrapper>
    </Modal>
  );
};

export default ITeroTermsConditionsModal;
